import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Autocomplete,
  Checkbox,
  TextField,
} from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { RoleOptionUi } from "../../types/documentManagementUiTypes";
import { parseRoleAccess } from "../../utils/documentManagementUiUtils";

const ALL_ROLES_VALUE = "*";
const ALL_ROLES_MARKER = "__all_roles__";
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

type Props = {
  roleAccessRaw: string;
  roleOptions: RoleOptionUi[];
  disabled?: boolean;
  onChange: (roleAccess: string[]) => void;
};

export function DocumentRoleAccessAutocomplete({
  roleAccessRaw,
  roleOptions,
  disabled,
  onChange,
}: Props) {
  const { t } = useTranslation();
  const parsed = parseRoleAccess(roleAccessRaw);
  const allRoleNames = useMemo(
    () => roleOptions.map((role) => role.name),
    [roleOptions]
  );
  const explicitRoles = parsed.filter((value) => value !== ALL_ROLES_VALUE);

  const isFullRoleSelection = (roles: string[]) =>
    allRoleNames.length > 0 &&
    allRoleNames.every((name) => roles.includes(name));

  const allRolesSelected =
    parsed.includes(ALL_ROLES_VALUE) || isFullRoleSelection(explicitRoles);

  const autocompleteOptions = useMemo(
    () => [ALL_ROLES_MARKER, ...allRoleNames],
    [allRoleNames]
  );

  const autocompleteValue = allRolesSelected
    ? [ALL_ROLES_MARKER, ...allRoleNames]
    : explicitRoles;

  const allRolesLabel = t("opva.dmAllRoles", {
    defaultValue: "All roles (unrestricted)",
  });
  const selectRolesPlaceholder = t("opva.dmSelectRoles", {
    defaultValue: "Select roles",
  });

  const handleChange = (_event: unknown, newValue: string[]) => {
    const nextHasAll = newValue.includes(ALL_ROLES_MARKER);
    const nextRoles = newValue.filter((value) => value !== ALL_ROLES_MARKER);

    if (nextHasAll && !allRolesSelected) {
      onChange([ALL_ROLES_VALUE]);
      return;
    }

    if (allRolesSelected && !nextHasAll) {
      onChange([]);
      return;
    }

    if (allRolesSelected && nextHasAll && !isFullRoleSelection(nextRoles)) {
      onChange(nextRoles);
      return;
    }

    if (!nextHasAll && isFullRoleSelection(nextRoles)) {
      onChange([ALL_ROLES_VALUE]);
      return;
    }

    onChange(nextRoles);
  };

  return (
    <Autocomplete
      multiple
      fullWidth
      limitTags={1}
      className={autocompleteValue.length > 0 ? "auto-complete-chip" : ""}
      disabled={disabled || allRoleNames.length === 0}
      options={autocompleteOptions}
      value={autocompleteValue}
      onChange={handleChange}
      disableCloseOnSelect
      getOptionLabel={(option) =>
        option === ALL_ROLES_MARKER ? allRolesLabel : option
      }
      isOptionEqualToValue={(option, value) => option === value}
      renderOption={(props, option, { selected }) => {
        const isAllOption = option === ALL_ROLES_MARKER;
        const checked = isAllOption
          ? allRolesSelected
          : allRolesSelected || selected;
        return (
          <li {...props} key={option}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={checked}
            />
            {isAllOption ? allRolesLabel : option}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder={selectRolesPlaceholder}
        />
      )}
    />
  );
}
